<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import {
  fetchMap, fetchPosition, moveTo,
  type MapData, type DistrictInfo, type MobData, type Nerve,
} from '../game/map-api';
import { mobImg, crestImg, classImg, crestArt, mobArt } from '../game/art';
import cashIcon from '../assets/omerta/icons/cash.webp';

/** Head-biased square crop of a full-body portrait, clipped into a disc.
 *  The stylized-3D character art is full-body on dark charcoal — the face sits
 *  in the upper ~quarter, so we crop a 56%-width square starting 6% down. */
function drawPortraitDisc(
  ctx: CanvasRenderingContext2D, im: HTMLImageElement,
  cx: number, cy: number, rad: number,
) {
  const w = im.naturalWidth, h = im.naturalHeight;
  const s = Math.min(w * 0.56, h);
  const sx = (w - s) / 2;
  const sy = Math.min(h * 0.06, h - s);
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  // dark felt behind the cutout so charcoal edges melt into the disc
  ctx.fillStyle = '#111017';
  ctx.fill();
  ctx.drawImage(im, sx, sy, s, s, cx - rad, cy - rad, rad * 2, rad * 2);
  ctx.restore();
}

/** Slim layered "brass seat" ring: dark outer seat, metal band with a top
 *  sheen, dark inner hairline. Band colour = family accent (or brass). */
function drawDecoRing(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, rad: number, band: string | CanvasGradient,
) {
  const lw = Math.min(Math.max(1.2, rad * 0.09), 2.4);
  // dark seat just outside the band grounds the disc on any tile colour
  ctx.beginPath();
  ctx.arc(cx, cy, rad + lw * 0.9, 0, Math.PI * 2);
  ctx.lineWidth = lw * 1.1;
  ctx.strokeStyle = 'rgba(6,5,10,0.85)';
  ctx.stroke();
  // the metal band
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.lineWidth = lw;
  ctx.strokeStyle = band;
  ctx.stroke();
  // top sheen — makes the band read as turned metal, not a flat outline
  const sheen = ctx.createLinearGradient(cx, cy - rad, cx, cy + rad);
  sheen.addColorStop(0, 'rgba(255,244,214,0.55)');
  sheen.addColorStop(0.45, 'rgba(255,244,214,0.06)');
  sheen.addColorStop(1, 'rgba(0,0,0,0.28)');
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.lineWidth = lw;
  ctx.strokeStyle = sheen;
  ctx.stroke();
  // inner hairline settles the portrait into its bezel
  ctx.beginPath();
  ctx.arc(cx, cy, rad - lw * 0.8, 0, Math.PI * 2);
  ctx.lineWidth = Math.max(0.8, lw * 0.45);
  ctx.strokeStyle = 'rgba(10,8,14,0.8)';
  ctx.stroke();
}

/** Brass band gradient for the player's own marker. */
function brassBand(ctx: CanvasRenderingContext2D, cx: number, cy: number, rad: number): CanvasGradient {
  const g = ctx.createLinearGradient(cx, cy - rad, cx, cy + rad);
  g.addColorStop(0, '#f0dda4');
  g.addColorStop(0.55, '#c8a24c');
  g.addColorStop(1, '#96742f');
  return g;
}
import nerveIcon from '../assets/omerta/icons/nerve.webp';
import { districtAccent, accentRgba } from '../game/district-accent';
import FightScreen from './FightScreen.vue';
import { startFight, startGarrison, type FightView } from '../game/mobfight-api';
import { useRegenClock, fmtEta } from '../game/regen-clock';
import QuickRecover from './QuickRecover.vue';

const props = defineProps<{
  heroClass?: string; heroLevel?: number; heroId?: string;
  heroHp?: number; heroMaxHp?: number; heroFullHp?: number; cash?: number; goldBars?: number;
  heroHpRegen?: { exact: number; perMin: number };
  /** District to smoothly pan the camera to on open ("Take it on the map"). */
  focusDistrict?: string | null;
}>();
const emit = defineEmits<{ (e: 'nerve', n: Nerve): void; (e: 'updated'): void; (e: 'focused'): void }>();

/** A quick-recover purchase changed nerve/HP/cash — refresh the HUD + parent. */
async function onRecovered() {
  try {
    const p = await fetchPosition();
    nerve.value = p.nerve;
    emit('nerve', p.nerve);
  } catch { /* HUD refreshes on the next move anyway */ }
  emit('updated');
}

// ── Hex geometry (ported from dominion HexMap: pointy-top, odd-row offset) ──
const HEX_R = 22;
const HEX_W = Math.sqrt(3) * HEX_R; // ~38.1 px

// ── City terrain palette (same layout semantics as dominion's biome palette:
// block=grass street=road park=forest slums=swamp rubble=ruins warzone=undead
// highrise=mountain stronghold=territory) ──
const TILE_COLOR: Record<string, string> = {
  block: '#4f4a42', park: '#3f6144', highrise: '#26242c', river: '#2c5a80',
  bridge: '#96793f', slums: '#5c5236', rubble: '#6e5f4d', stronghold: '#14121a',
  street: '#7d7668', warzone: '#4a2a30',
};
const TILE_BORDER: Record<string, string> = {
  block: '#403c35', park: '#2f5134', highrise: '#45424f', river: '#1d4a6e',
  bridge: '#86692f', slums: '#4c4226', rubble: '#5e4f3d', stronghold: '#c9913a',
  street: '#6d6658', warzone: '#3a1a20',
};
const TILE_COST: Record<string, number> = {
  block: 1, street: 1, stronghold: 1, bridge: 1, rubble: 1,
  park: 2, slums: 2, warzone: 2,
  highrise: Infinity, river: Infinity,
};
const IMPASSABLE = new Set(['highrise', 'river']);

// ── State ──
const canvasRef = ref<HTMLCanvasElement | null>(null);
const minimapRef = ref<HTMLCanvasElement | null>(null);
const loading = ref(true);
const error = ref('');
const nerve = ref<Nerve>({ current: 0, max: 0 });
/** Countdown to the next nerve pip — anchors ride along on every position/move payload. */
const nerveClock = useRegenClock(() =>
  nerve.value.max > 0 ? { anchor: nerve.value.regen, max: nerve.value.max } : null);
const posInfo = ref({ col: 0, row: 0 });
const hoverName = ref<string>('');       // district or mob label for the info bar
const hoverCost = ref<number | null>(null);
const hoverBlocked = ref(false);
const fighting = ref(false);
const showHelp = ref(false);             // "how to run the city" card toggle
const tacticalTarget = ref<{ col: number; row: number } | null>(null);
const garrisonTarget = ref<string | null>(null); // districtId for a turf takeover
const busyFight = ref(false);                    // start-* in flight (guards double-open)
const fightInitial = ref<FightView | null>(null); // the bout we started, handed to FightScreen

let map: MapData | null = null;
let COLS = 64;
let ROWS = 74;

const camera = { x: 0, y: 0, scale: 1.0 };
let centered = false;
const drag = { active: false, sx: 0, sy: 0, cx: 0, cy: 0, moved: false };
let hovered: [number, number] | null = null;
let hovPath: [number, number][] = [];
const pos = { col: 0, row: 0 };
// Fog of war — cells the hero has ever seen; within SIGHT of the hero = full.
const SIGHT = 5;
let exploredSet = new Set<string>();
// Mafia turf partition: every hex → index into `districts` (client voronoi).
let districts: DistrictInfo[] = [];
let turfIdx: Uint8Array = new Uint8Array(0); // row * COLS + col → district index
let labels: { district: DistrictInfo; x: number; y: number }[] = [];
let animTime = 0;
let rafId = 0;

// Sentinel turfIdx value meaning "this hex's district isn't in our (possibly
// partial) summary" — always out of range since there are only 8 districts.
const NO_TURF = 255;

function turfOf(col: number, row: number): DistrictInfo | undefined {
  const idx = turfIdx[row * COLS + col];
  return idx < districts.length ? districts[idx] : undefined;
}

/** Human-readable "where the hero is standing" — the turf name, or the no-man's
 *  streets between turfs. Drives the HUD location plate (replaces raw col/row). */
const locationName = computed(() => {
  void posInfo.value; // re-evaluate whenever the hero moves
  return turfOf(posInfo.value.col, posInfo.value.row)?.name ?? 'The open streets';
});

/** The district whose STRONGHOLD (objective hex) sits on (col,row), if any.
 *  Only matches districts present in the (possibly partial — seen-only)
 *  summary; an un-scouted stronghold falls through to a plain walk. */
function strongholdAt(col: number, row: number): DistrictInfo | undefined {
  return districts.find((d) => d.col === col && d.row === row);
}

/** (Re)build the turf partition + labels from a map payload. Labels sit on each
 *  district's stronghold — the objective — not a floating centroid.
 *  `m.districts` may now be a PARTIAL summary (seen districts only) — tiles
 *  whose districtId isn't in that summary get the NO_TURF sentinel rather
 *  than silently aliasing to district 0. */
function hydrateMap(m: MapData) {
  districts = m.districts ?? [];
  const idxOf = new Map(districts.map((d, i) => [d.id, i]));
  turfIdx = new Uint8Array(m.cols * m.rows);
  for (let r = 0; r < m.rows; r++) {
    for (let c = 0; c < m.cols; c++) {
      const id = m.tiles[r][c].districtId;
      turfIdx[r * m.cols + c] = id !== undefined ? idxOf.get(id) ?? NO_TURF : NO_TURF;
    }
  }
  labels = districts.map((district) => {
    const [x, y] = hexCenter(district.col, district.row);
    return { district, x, y };
  });
}

// ── Territory-entry notices ────────────────────────────────────────────────
// First time the hero sets foot on a family's turf we serve a calling card:
// district, family, tier — and an honest read on whether he's outgunned.
// "Seen" turfs persist per hero in localStorage; a deadly turf re-warns once
// per session even after it's been seen.
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
type TurfDanger = 'safe' | 'risky' | 'deadly';
const turfNotice = ref<{ d: DistrictInfo; danger: TurfDanger; crest?: string } | null>(null);
let turfNoticeTimer = 0;
let lastTurfId: string | null | undefined; // undefined until the first check
const seenKey = () => `mafinini.turf-seen.${props.heroId ?? 'anon'}`;
let seenTurfs = new Set<string>();
try { seenTurfs = new Set<string>(JSON.parse(localStorage.getItem(seenKey()) ?? '[]')); } catch { /* fresh ledger */ }
const warnedThisSession = new Set<string>();

function dangerOf(d: DistrictInfo): TurfDanger {
  const lvl = props.heroLevel;
  if (lvl == null) return 'safe';
  if (lvl < d.unlockLevel - 2) return 'deadly';
  if (lvl < d.unlockLevel) return 'risky';
  return 'safe';
}

/**
 * Announce a turf crossing — ONLY ever called after an actual move.
 *
 * `prime` records where the hero already is WITHOUT announcing anything. The
 * map (re)loads with `lastTurfId` unset, so the first check after a load looks
 * exactly like a transition; without priming, merely opening the City Map
 * announced "You've crossed into <turf>" for the turf the hero was already
 * standing on. Same for a refresh — neither is a crossing.
 */
function checkTurfEntry(prime = false) {
  const t = turfOf(pos.col, pos.row);
  const id = t?.id ?? null;
  if (id === lastTurfId) return;
  lastTurfId = id;
  if (prime || !t) return;
  const danger = dangerOf(t);
  const isNew = !seenTurfs.has(t.id);
  const reWarn = danger === 'deadly' && !warnedThisSession.has(t.id);
  if (!isNew && !reWarn) return;
  if (isNew) {
    seenTurfs.add(t.id);
    try { localStorage.setItem(seenKey(), JSON.stringify([...seenTurfs])); } catch { /* private mode */ }
  }
  if (danger !== 'safe') warnedThisSession.add(t.id);
  turfNotice.value = { d: t, danger, crest: crestArt(t.family) };
  window.clearTimeout(turfNoticeTimer);
  turfNoticeTimer = window.setTimeout(() => { turfNotice.value = null; }, 8000);
}

/** The district summary is fog-gated server-side (names ship once any cell of
 *  the turf is explored) but was only fetched on mount — so walking INTO fresh
 *  territory revealed nameless, untinted hexes (and a bare stronghold) until a
 *  reload. When newly explored tiles reference a district we don't know yet,
 *  refetch the map so the name, tint, label and calling card arrive live. */
async function maybeRefreshDistricts() {
  if (!map) return;
  const known = new Set(districts.map((d) => d.id));
  let unknown = false;
  for (const cell of exploredSet) {
    const i = cell.indexOf(',');
    const id = map.tiles[+cell.slice(i + 1)]?.[+cell.slice(0, i)]?.districtId;
    if (id && !known.has(id)) { unknown = true; break; }
  }
  if (!unknown) return;
  try {
    map = await fetchMap();
    hydrateMap(map);
  } catch { /* keep the stale summary — next move retries */ }
}

// Fog state of a hex: 'full' (in sight now), 'dim' (explored, out of sight), 'fog' (never seen).
function fogOf(col: number, row: number): 'full' | 'dim' | 'fog' {
  if (hexDist(pos.col, pos.row, col, row) <= SIGHT) return 'full';
  return exploredSet.has(`${col},${row}`) ? 'dim' : 'fog';
}

// ── Hex math ──
function hexCenter(col: number, row: number): [number, number] {
  return [col * HEX_W + ((row & 1) ? HEX_W / 2 : 0), row * HEX_R * 1.5];
}
function hexPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = Math.PI / 6 + (Math.PI / 3) * i;
    const x = cx + r * Math.cos(a), y = cy + r * Math.sin(a);
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.closePath();
}
function pixelToHex(wx: number, wy: number): [number, number] {
  const rowEst = wy / (HEX_R * 1.5);
  let bestC = 0, bestR = 0, bestD = Infinity;
  for (let dr = -1; dr <= 1; dr++) {
    const r = Math.round(rowEst) + dr;
    if (r < 0 || r >= ROWS) continue;
    const offset = (r & 1) ? HEX_W / 2 : 0;
    const cEst = (wx - offset) / HEX_W;
    for (let dc = -1; dc <= 1; dc++) {
      const c = Math.round(cEst) + dc;
      if (c < 0 || c >= COLS) continue;
      const [hx, hy] = hexCenter(c, r);
      const d = (wx - hx) ** 2 + (wy - hy) ** 2;
      if (d < bestD) { bestD = d; bestC = c; bestR = r; }
    }
  }
  return [bestC, bestR];
}
function hexDist(c1: number, r1: number, c2: number, r2: number): number {
  const q1 = c1 - Math.floor((r1 - (r1 & 1)) / 2), s1 = r1;
  const q2 = c2 - Math.floor((r2 - (r2 & 1)) / 2), s2 = r2;
  const dq = q1 - q2, ds = s1 - s2;
  return Math.max(Math.abs(dq), Math.abs(ds), Math.abs(dq + ds));
}
function hexNeighborOffsets(row: number): [number, number][] {
  return (row & 1)
    ? [[1, 0], [-1, 0], [1, -1], [0, -1], [1, 1], [0, 1]]
    : [[1, 0], [-1, 0], [0, -1], [-1, -1], [0, 1], [-1, 1]];
}
// Client-side A* path preview (matches dominion — cosmetic + nerve estimate)
function clientAstar(sc: number, sr: number, ec: number, er: number): [number, number][] {
  if (!map) return [];
  const tiles = map.tiles;
  type N = { c: number; r: number; g: number; f: number; prev: N | null };
  const key = (c: number, r: number) => c * 1000 + r;
  const open = new Map<number, N>();
  const closed = new Set<number>();
  open.set(key(sc, sr), { c: sc, r: sr, g: 0, f: hexDist(sc, sr, ec, er), prev: null });
  for (let i = 0; i < 500 && open.size > 0; i++) {
    let cur: N | null = null;
    for (const n of open.values()) if (!cur || n.f < cur.f) cur = n;
    if (!cur) break;
    if (cur.c === ec && cur.r === er) {
      const path: [number, number][] = [];
      let n: N | null = cur;
      while (n) { path.unshift([n.c, n.r]); n = n.prev; }
      return path;
    }
    open.delete(key(cur.c, cur.r));
    closed.add(key(cur.c, cur.r));
    for (const [dc, dr] of hexNeighborOffsets(cur.r)) {
      const nc = cur.c + dc, nr = cur.r + dr;
      if (nc < 0 || nc >= COLS || nr < 0 || nr >= ROWS) continue;
      if (closed.has(key(nc, nr))) continue;
      const cost = TILE_COST[tiles[nr]?.[nc]?.type ?? 'block'] ?? 1;
      if (!isFinite(cost)) continue;
      const g = cur.g + cost;
      if (g > 10) continue;
      const ex = open.get(key(nc, nr));
      if (!ex || g < ex.g)
        open.set(key(nc, nr), { c: nc, r: nr, g, f: g + hexDist(nc, nr, ec, er), prev: cur });
    }
  }
  return [];
}

// ── Draw ──
function draw() {
  const canvas = canvasRef.value;
  if (!canvas || !map) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const W = canvas.clientWidth, H = canvas.clientHeight;
  if (canvas.width !== Math.round(W * dpr) || canvas.height !== Math.round(H * dpr)) {
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
  }
  if (W === 0 || H === 0) return;

  const { x: panX, y: panY, scale } = camera;
  const tiles = map.tiles;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = '#0a0714';
  ctx.fillRect(0, 0, W, H);

  ctx.save();
  ctx.translate(W / 2 + panX, H / 2 + panY);
  ctx.scale(scale, scale);

  // Viewport-culled tile bounds
  const wl = (-W / 2 - panX) / scale, wt = (-H / 2 - panY) / scale;
  const wr = (W / 2 - panX) / scale, wb = (H / 2 - panY) / scale;
  const c0 = Math.max(0, Math.floor(wl / HEX_W) - 2);
  const c1 = Math.min(COLS - 1, Math.ceil(wr / HEX_W) + 2);
  const r0 = Math.max(0, Math.floor(wt / (HEX_R * 1.5)) - 2);
  const r1 = Math.min(ROWS - 1, Math.ceil(wb / (HEX_R * 1.5)) + 2);

  const pathSet = new Set<string>();
  for (const [pc, pr] of hovPath) pathSet.add(`${pc},${pr}`);
  const hov = hovered;

  for (let row = r0; row <= r1; row++) {
    for (let col = c0; col <= c1; col++) {
      const [cx, cy] = hexCenter(col, row);
      const tile = tiles[row]?.[col];
      const type = tile?.type ?? 'block';
      const turf = turfOf(col, row);

      ctx.fillStyle = TILE_COLOR[type] ?? '#4f4a42';
      hexPath(ctx, cx, cy, HEX_R - 0.5);
      ctx.fill();

      // Mafia turf tint — the whole city is carved into family territories
      if (turf) {
        hexPath(ctx, cx, cy, HEX_R - 0.5);
        ctx.fillStyle = accentRgba(districtAccent(turf.id), 0.11);
        ctx.fill();
      }

      if (pathSet.has(`${col},${row}`)) {
        hexPath(ctx, cx, cy, HEX_R - 0.5);
        ctx.fillStyle = 'rgba(251,191,36,0.32)';
        ctx.fill();
      }
      if (hov && hov[0] === col && hov[1] === row) {
        hexPath(ctx, cx, cy, HEX_R - 0.5);
        ctx.fillStyle = 'rgba(255,255,255,0.15)';
        ctx.fill();
      }

      hexPath(ctx, cx, cy, HEX_R - 0.5);
      ctx.strokeStyle = TILE_BORDER[type] ?? '#403c35';
      ctx.lineWidth = scale < 0.6 ? 0.3 : 0.7;
      ctx.stroke();

      // Turf boundary — thick ink line on edges facing another family's turf
      if (turf) {
        const myIdx = turfIdx[row * COLS + col];
        for (const [dc, dr] of hexNeighborOffsets(row)) {
          const nc = col + dc, nr = row + dr;
          if (nc < 0 || nc >= COLS || nr < 0 || nr >= ROWS) continue;
          if (turfIdx[nr * COLS + nc] === myIdx) continue;
          // shared edge = the two hex corners nearest the neighbor's center
          const [nx, ny] = hexCenter(nc, nr);
          let a1 = -1, a2 = -1, d1 = Infinity, d2 = Infinity;
          for (let i = 0; i < 6; i++) {
            const ang = Math.PI / 6 + (Math.PI / 3) * i;
            const px = cx + (HEX_R - 0.5) * Math.cos(ang);
            const py = cy + (HEX_R - 0.5) * Math.sin(ang);
            const d = (px - nx) ** 2 + (py - ny) ** 2;
            if (d < d1) { d2 = d1; a2 = a1; d1 = d; a1 = i; }
            else if (d < d2) { d2 = d; a2 = i; }
          }
          const ang1 = Math.PI / 6 + (Math.PI / 3) * a1;
          const ang2 = Math.PI / 6 + (Math.PI / 3) * a2;
          ctx.beginPath();
          ctx.moveTo(cx + (HEX_R - 0.5) * Math.cos(ang1), cy + (HEX_R - 0.5) * Math.sin(ang1));
          ctx.lineTo(cx + (HEX_R - 0.5) * Math.cos(ang2), cy + (HEX_R - 0.5) * Math.sin(ang2));
          ctx.strokeStyle = 'rgba(8,6,14,0.85)';
          ctx.lineWidth = 3;
          ctx.stroke();
        }
      }

      // Family unit marker — only within sight; bob gently; disc in family accent.
      const fog = fogOf(col, row);
      if (tile?.mob && HEX_R * scale >= 10 && fog === 'full') {
        const lvl = Math.min(tile.mob.level, 15);
        const rad = HEX_R * 0.34 + (lvl / 15) * HEX_R * 0.2;
        const bob = Math.sin(animTime / 700 + col * 1.3 + row * 0.9) * 2;
        const accent = districtAccent(tile.districtId ?? turf?.id);
        const portrait = mobImg(tile.mob.type, tile.mob.archetype);
        if (portrait) {
          // Enemy head-shot in a slim family-accent bezel.
          drawPortraitDisc(ctx, portrait, cx, cy + bob, rad);
          drawDecoRing(ctx, cx, cy + bob, rad, accent);
        } else {
          // Fallback: family-accent disc (art not yet loaded / unknown archetype).
          ctx.beginPath();
          ctx.arc(cx, cy + bob, rad, 0, Math.PI * 2);
          ctx.fillStyle = accent;
          ctx.fill();
          ctx.lineWidth = 1.4;
          ctx.strokeStyle = 'rgba(8,8,12,0.85)';
          ctx.stroke();
        }
      }

      // Fog of war overlay — dim what's been seen but is out of sight; hide the rest.
      if (fog === 'dim') {
        hexPath(ctx, cx, cy, HEX_R - 0.3);
        ctx.fillStyle = 'rgba(0,0,10,0.58)';
        ctx.fill();
      } else if (fog === 'fog') {
        hexPath(ctx, cx, cy, HEX_R - 0.3);
        ctx.fillStyle = 'rgba(0,0,8,0.92)';
        ctx.fill();
      }
    }
  }

  // Mafia turf labels sit ON each stronghold (the objective): a ring marks the
  // exact capture hex, the crest + family + district + tier/lock read above it.
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const inv = 1 / Math.max(scale, 0.5);
  const fs = 19 * inv; // ~constant on-screen size
  for (const l of labels) {
    const accent = districtAccent(l.district.id);
    const objColor = l.district.locked ? '#c07070' : l.district.owner === 'you' ? '#e6c15a' : accent;

    // Objective ring on the stronghold hex — pulses gently so it reads as a goal.
    const pulse = 1 + 0.08 * Math.sin(animTime / 500);
    ctx.save();
    ctx.beginPath();
    ctx.arc(l.x, l.y, HEX_R * 0.74 * pulse, 0, Math.PI * 2);
    ctx.lineWidth = 3 * inv;
    ctx.strokeStyle = accentRgba(objColor, 0.9);
    ctx.setLineDash([7 * inv, 5 * inv]);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // Family crest above the ring (round, accent border).
    const crest = crestImg(l.district.family);
    const cs = fs * 2.3;
    const ccy = l.y - HEX_R * 0.72 - cs * 0.5;
    if (crest) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(l.x, ccy, cs / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(crest, l.x - cs / 2, ccy - cs / 2, cs, cs);
      ctx.restore();
      drawDecoRing(ctx, l.x, ccy, cs / 2, objColor);
    }

    // Text block below the ring.
    const ty = l.y + HEX_R * 0.72 + fs * 0.7;
    ctx.lineWidth = 4 * inv;
    ctx.strokeStyle = 'rgba(6,6,10,0.95)';
    ctx.font = `bold ${fs}px "Bodoni Moda", Georgia, serif`;
    ctx.strokeText(l.district.family.toUpperCase(), l.x, ty);
    ctx.fillStyle = accent;
    ctx.fillText(l.district.family.toUpperCase(), l.x, ty);

    ctx.font = `${fs * 0.7}px "Bodoni Moda", Georgia, serif`;
    ctx.strokeText(l.district.name.toUpperCase(), l.x, ty + fs * 0.95);
    ctx.fillStyle = '#efe4c4';
    ctx.fillText(l.district.name.toUpperCase(), l.x, ty + fs * 0.95);

    const tag = l.district.owner === 'you'
      ? `TIER ${l.district.tier} · YOUR TURF`
      : l.district.locked
        ? `TIER ${l.district.tier} · LOCKED · LV ${l.district.unlockLevel}`
        : `TIER ${l.district.tier} · TAKE IT`;
    ctx.font = `bold ${fs * 0.54}px "Courier Prime", "Courier New", monospace`;
    ctx.strokeText(tag, l.x, ty + fs * 1.72);
    ctx.fillStyle = l.district.owner === 'you' ? '#e6c15a' : l.district.locked ? '#d78a8a' : 'rgba(236,227,207,0.82)';
    ctx.fillText(tag, l.x, ty + fs * 1.72);
  }

  // Impassable indicator (red X on hovered highrise/river)
  if (hov) {
    const [hc, hr] = hov;
    const hovType = tiles[hr]?.[hc]?.type ?? 'block';
    if (IMPASSABLE.has(hovType)) {
      const [cx, cy] = hexCenter(hc, hr);
      const xr = HEX_R * 0.42;
      ctx.strokeStyle = 'rgba(239,68,68,0.9)';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(cx - xr, cy - xr); ctx.lineTo(cx + xr, cy + xr); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx + xr, cy - xr); ctx.lineTo(cx - xr, cy + xr); ctx.stroke();
    }
  }

  // Player marker — the hero's own portrait in a brass bezel, under a soft
  // breathing gold halo so you always find yourself on the board.
  {
    const [cx, cy] = hexCenter(pos.col, pos.row);
    const r = Math.max(8, HEX_R * 0.5);
    const breathe = 1 + 0.06 * Math.sin(animTime / 600);
    ctx.save();
    // halo
    const halo = ctx.createRadialGradient(cx, cy, r * 0.6, cx, cy, r * 2.1 * breathe);
    halo.addColorStop(0, 'rgba(227,200,120,0.32)');
    halo.addColorStop(1, 'rgba(227,200,120,0)');
    ctx.fillStyle = halo;
    ctx.beginPath(); ctx.arc(cx, cy, r * 2.1 * breathe, 0, Math.PI * 2); ctx.fill();
    // pulsing outer ring keeps the old "you are here" read
    ctx.strokeStyle = 'rgba(251,191,36,0.45)';
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(cx, cy, r + 5 * breathe, 0, Math.PI * 2); ctx.stroke();

    const me = classImg(props.heroClass);
    if (me) {
      drawPortraitDisc(ctx, me, cx, cy, r);
      drawDecoRing(ctx, cx, cy, r, brassBand(ctx, cx, cy, r));
      // tiny brass pin under the bezel — "this piece is yours"
      const ps = Math.max(3, r * 0.22);
      ctx.translate(cx, cy + r + ps * 0.9);
      ctx.rotate(Math.PI / 4);
      ctx.fillStyle = '#e3c878';
      ctx.strokeStyle = 'rgba(6,5,10,0.85)';
      ctx.lineWidth = 1;
      ctx.fillRect(-ps / 2, -ps / 2, ps, ps);
      ctx.strokeRect(-ps / 2, -ps / 2, ps, ps);
    } else {
      // portrait not loaded yet — fall back to the old gold disc
      ctx.fillStyle = '#e3c878';
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#7a1620';
      ctx.beginPath(); ctx.arc(cx, cy, r * 0.4, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
  }

  ctx.restore();

  // Minimap
  const mm = minimapRef.value;
  if (mm) {
    const mc = mm.getContext('2d');
    if (mc) {
      const MM = 148;
      const tw = MM / COLS, th = MM / ROWS;
      mc.clearRect(0, 0, MM, MM);
      for (let r = 0; r < ROWS; r++)
        for (let c = 0; c < COLS; c++) {
          mc.fillStyle = TILE_COLOR[tiles[r]?.[c]?.type ?? 'block'];
          mc.fillRect(Math.floor(c * tw), Math.floor(r * th), Math.ceil(tw) + 1, Math.ceil(th) + 1);
          const turf = turfOf(c, r);
          if (turf) {
            mc.fillStyle = accentRgba(districtAccent(turf.id), 0.28);
            mc.fillRect(Math.floor(c * tw), Math.floor(r * th), Math.ceil(tw) + 1, Math.ceil(th) + 1);
          }
        }
      mc.fillStyle = '#e3c878';
      mc.beginPath();
      mc.arc((pos.col + 0.5) * tw, (pos.row + 0.5) * th, 3, 0, Math.PI * 2);
      mc.fill();
    }
  }
}

// ── Camera helpers ──
function centerOnHero() {
  cancelAnimationFrame(panRaf);
  const [hx, hy] = hexCenter(pos.col, pos.row);
  camera.x = -(hx * camera.scale);
  camera.y = -(hy * camera.scale);
  draw();
}

// ── Smooth camera pan (eased) to a hex, e.g. a district's stronghold ──────────
let panRaf = 0;
let focusPanned = false;
function panCameraTo(col: number, row: number, duration = 750) {
  cancelAnimationFrame(panRaf);
  const [tx, ty] = hexCenter(col, row);
  const targetX = -(tx * camera.scale);
  const targetY = -(ty * camera.scale);
  const startX = camera.x, startY = camera.y;
  const dx = targetX - startX, dy = targetY - startY;
  const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || (Math.abs(dx) < 1 && Math.abs(dy) < 1)) {
    camera.x = targetX; camera.y = targetY; draw(); return;
  }
  const ease = (t: number) => 1 - Math.pow(1 - t, 3); // easeOutCubic
  let t0: number | null = null;
  const step = (now: number) => {
    if (t0 === null) t0 = now;
    const p = Math.min(1, (now - t0) / duration);
    const e = ease(p);
    camera.x = startX + dx * e;
    camera.y = startY + dy * e;
    draw();
    if (p < 1) panRaf = requestAnimationFrame(step);
  };
  panRaf = requestAnimationFrame(step);
}

/** One-shot: on "Take it on the map", start on the hero, then glide to the
 *  target district's stronghold. Consumed once (clears the parent's focus). */
function maybeFocusPan() {
  if (focusPanned || !props.focusDistrict) return;
  focusPanned = true;
  const d = districts.find((x) => x.id === props.focusDistrict);
  if (d) panCameraTo(d.col, d.row);
  emit('focused');
}

// ── Interaction ──
function updateHover(col: number, row: number) {
  if (!map) return;
  const prev = hovered;
  if (prev && prev[0] === col && prev[1] === row) return;
  hovered = [col, row];
  hovPath = clientAstar(pos.col, pos.row, col, row);
  const tile = map.tiles[row]?.[col];
  const type = tile?.type ?? 'block';
  const impassable = IMPASSABLE.has(type);
  hoverBlocked.value = impassable;
  if (impassable) {
    hoverCost.value = null;
  } else if (hovPath.length > 1) {
    hoverCost.value = hovPath.slice(1).reduce(
      (s, [pc, pr]) => s + (TILE_COST[map!.tiles[pr]?.[pc]?.type ?? 'block'] ?? 1), 0);
  } else {
    hoverCost.value = null;
  }
  // Turf name for the hover readout: prefer the full summary (family + name)
  // when we have it; the tile itself always carries a plain districtName
  // (baked at map gen, independent of the possibly-partial seen-only summary),
  // so hover still resolves a name for turf we haven't scouted into `districts` yet.
  const turf = turfOf(col, row);
  hoverName.value = tile?.mob
    ? `${tile.mob.name} · Lv ${tile.mob.level} · ${tile.mob.cash} cash`
    : turf
      ? `${turf.family} — ${turf.name}`
      : (tile?.districtName ?? '');
  draw();
}

function screenToWorld(clientX: number, clientY: number): [number, number] {
  const canvas = canvasRef.value!;
  const rect = canvas.getBoundingClientRect();
  const relX = clientX - rect.left, relY = clientY - rect.top;
  const W = canvas.clientWidth, H = canvas.clientHeight;
  return [
    (relX - W / 2 - camera.x) / camera.scale,
    (relY - H / 2 - camera.y) / camera.scale,
  ];
}

async function doMove(col: number, row: number) {
  try {
    const res = await moveTo(col, row);
    pos.col = res.col; pos.row = res.row;
    posInfo.value = { col: res.col, row: res.row };
    nerve.value = res.nerve;
    if (res.exploredCells) exploredSet = new Set(res.exploredCells);
    emit('nerve', res.nerve);
    hovPath = [];
    await maybeRefreshDistricts();
    checkTurfEntry();
    draw();
  } catch (err) {
    error.value = (err as Error).message;
  }
}

// ── Pre-fight calling card — every fight is confirmed before blood spills ──
const confirmFight = ref<
  | { kind: 'mob'; col: number; row: number; mob: MobData }
  | { kind: 'garrison'; d: DistrictInfo }
  | null
>(null);

// Ghost-tap guard (QA, mobile): the tap that opens the card can land where the
// gold button appears — pointerup then fires "Start the fight" instantly. Arm
// the button only 500ms after the card opens so the opening tap can't spend it.
const confirmArmed = ref(false);
let confirmArmTimer = 0;
watch(confirmFight, (v) => {
  window.clearTimeout(confirmArmTimer);
  confirmArmed.value = false;
  if (v) confirmArmTimer = window.setTimeout(() => { confirmArmed.value = true; }, 500);
});

/** Honest odds read vs the hero's level (same tone as the turf notices). */
function fightOdds(lvl: number): { label: string; cls: string; note: string } {
  const diff = lvl - (props.heroLevel ?? 1);
  if (diff >= 4) return { label: 'Deadly', cls: 'is-deadly', note: 'Far above your weight. Walking away is not cowardice.' };
  if (diff >= 2) return { label: 'Risky', cls: 'is-risky', note: 'They hit above your weight here. Bring your best.' };
  if (diff <= -3) return { label: 'Easy mark', cls: 'is-safe', note: 'This one will not take long.' };
  return { label: 'Fair fight', cls: 'is-safe', note: 'An even match — make it count.' };
}
const confirmOdds = computed(() =>
  confirmFight.value?.kind === 'mob' ? fightOdds(confirmFight.value.mob.level) : null);
const heroHpPct = computed(() =>
  props.heroHp != null && props.heroMaxHp ? Math.round((props.heroHp / props.heroMaxHp) * 100) : null);

/**
 * Open the bout — but START it here, not inside the fight screen.
 *
 * Starting is what validates: nerve, hero HP, the level/tier gate and the
 * spatial gate all reject at start-*. Doing it here means "Not enough nerve"
 * lands on the map, where the player still is, instead of throwing them onto a
 * fight screen with nothing on it but an error.
 */
async function onConfirmFight() {
  const c = confirmFight.value;
  if (!c || busyFight.value) return;
  busyFight.value = true;
  error.value = '';
  try {
    const v = c.kind === 'mob'
      ? await startFight(c.col, c.row)
      : await startGarrison(c.d.id);
    confirmFight.value = null;
    fightInitial.value = v;
    if (c.kind === 'mob') tacticalTarget.value = { col: c.col, row: c.row };
    else garrisonTarget.value = c.d.id;
    // start-* already spent the nerve server-side — surface it now, not at bout
    // end, so the header chip and HUD gauge don't lie for the whole fight.
    void fetchPosition()
      .then((p) => { nerve.value = p.nerve; emit('nerve', p.nerve); })
      .catch(() => { /* cosmetic — reconciled on fight close anyway */ });
  } catch (err) {
    confirmFight.value = null;
    error.value = (err as Error).message; // e.g. "Not enough nerve (need 1)."
  } finally {
    busyFight.value = false;
  }
}

// Clicking an enemy serves the calling card; accepting it opens the interactive
// tactical fight for (col,row). The arena drives the fight turn-by-turn against
// the server; we just refresh on close.
function fight(col: number, row: number) {
  error.value = '';
  const mob = map?.tiles[row]?.[col]?.mob;
  if (!mob) return;
  confirmFight.value = { kind: 'mob', col, row, mob };
}

async function onTacticalDone(_changed: boolean) {
  tacticalTarget.value = null;
  // Refresh UNCONDITIONALLY: `changed=false` only means the bout didn't settle
  // (a flee) — but nerve was spent at start-fight and HP was lost in the rounds,
  // so "nothing changed" is never true once a fight opened. Gating on it left
  // the header chips and the HUD nerve gauge reading pre-fight values.
  try {
    const [m, p] = await Promise.all([fetchMap(), fetchPosition()]);
    map = m;
    pos.col = p.col; pos.row = p.row; posInfo.value = { col: p.col, row: p.row };
    nerve.value = p.nerve;
    exploredSet = new Set(p.exploredCells ?? []);
    checkTurfEntry(true); // a refresh re-seats us on the same tile — not a crossing
    draw();
    emit('nerve', p.nerve); emit('updated');
  } catch (err) {
    error.value = (err as Error).message;
  }
}

// ── Pointer / wheel handlers ──
function onPointerDown(e: PointerEvent) {
  if (e.button !== 0) return;
  cancelAnimationFrame(panRaf); // user grabs the map — stop any auto-pan in flight
  canvasRef.value?.setPointerCapture(e.pointerId);
  drag.active = true; drag.moved = false;
  drag.sx = e.clientX; drag.sy = e.clientY;
  drag.cx = camera.x; drag.cy = camera.y;
}
function onPointerMove(e: PointerEvent) {
  if (!drag.active) {
    const [wx, wy] = screenToWorld(e.clientX, e.clientY);
    const [col, row] = pixelToHex(wx, wy);
    if (col >= 0 && col < COLS && row >= 0 && row < ROWS) updateHover(col, row);
    return;
  }
  const dx = e.clientX - drag.sx, dy = e.clientY - drag.sy;
  if (!drag.moved && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) drag.moved = true;
  if (drag.moved) { camera.x = drag.cx + dx; camera.y = drag.cy + dy; draw(); }
}
async function onPointerUp(e: PointerEvent) {
  if (!drag.active) return;
  const wasDrag = drag.moved;
  drag.active = false; drag.moved = false;
  if (wasDrag) return;
  if (fighting.value || tacticalTarget.value || garrisonTarget.value || confirmFight.value || !map) return;
  const [wx, wy] = screenToWorld(e.clientX, e.clientY);
  const [col, row] = pixelToHex(wx, wy);
  if (col < 0 || col >= COLS || row < 0 || row >= ROWS) return;
  error.value = '';
  const tile = map.tiles[row]?.[col];

  if (tile?.mob) {
    // Spatial combat: if already adjacent, fight; else walk to a reachable hex
    // next to the enemy along the A* route, then fight (dominion-style).
    if (hexDist(pos.col, pos.row, col, row) <= 1) { await fight(col, row); return; }
    const path = clientAstar(pos.col, pos.row, col, row);
    if (path.length < 2) { error.value = 'No route to that target.'; return; }
    const [ac, ar] = path[path.length - 2]; // hex just before the enemy
    await doMove(ac, ar);
    if (!error.value && hexDist(pos.col, pos.row, col, row) <= 1) await fight(col, row);
    return;
  }

  // Stronghold (turf objective): take the district with an interactive garrison
  // fight — but only by STANDING ON it. If you're not on it yet, walk there along
  // the route, then the takeover opens in the same click.
  const sh = strongholdAt(col, row);
  if (sh && sh.owner !== 'you') {
    if (sh.locked) {
      error.value = `${sh.name} is locked — reach level ${sh.unlockLevel} to take it.`;
      return;
    }
    if (pos.col === col && pos.row === row) { confirmFight.value = { kind: 'garrison', d: sh }; return; }
    await doMove(col, row);
    if (!error.value && pos.col === col && pos.row === row) confirmFight.value = { kind: 'garrison', d: sh };
    return;
  }

  // Empty hex: ignore impassable clicks; else walk there (server A* over terrain).
  if (tile && IMPASSABLE.has(tile.type)) return;
  await doMove(col, row);
}

/** Turf takeover finished — refresh ownership + position from the server.
 *  Unconditional for the same reason as onTacticalDone: a fled garrison bout
 *  still spent nerve at start and HP in the rounds. */
async function onCaptureDone(_changed: boolean) {
  garrisonTarget.value = null;
  try {
    const [m, p] = await Promise.all([fetchMap(), fetchPosition()]);
    map = m; hydrateMap(m);
    pos.col = p.col; pos.row = p.row; posInfo.value = { col: p.col, row: p.row };
    nerve.value = p.nerve; exploredSet = new Set(p.exploredCells ?? []);
    emit('nerve', p.nerve); emit('updated');
    draw();
  } catch (e) { error.value = (e as Error).message; }
}
function onPointerLeave() { hovered = null; hovPath = []; hoverName.value = ''; hoverCost.value = null; hoverBlocked.value = false; draw(); }
function onWheel(e: WheelEvent) {
  e.preventDefault();
  const canvas = canvasRef.value!;
  const rect = canvas.getBoundingClientRect();
  const W = canvas.clientWidth, H = canvas.clientHeight;
  const relX = e.clientX - rect.left, relY = e.clientY - rect.top;
  const wx0 = (relX - W / 2 - camera.x) / camera.scale;
  const wy0 = (relY - H / 2 - camera.y) / camera.scale;
  const factor = e.deltaY > 0 ? 0.94 : 1.06;
  const newScale = Math.min(3.5, Math.max(0.25, camera.scale * factor));
  camera.scale = newScale;
  camera.x = relX - W / 2 - wx0 * newScale;
  camera.y = relY - H / 2 - wy0 * newScale;
  draw();
}

let ro: ResizeObserver | null = null;

onMounted(async () => {
  try {
    const [m, p] = await Promise.all([fetchMap(), fetchPosition()]);
    map = m; COLS = m.cols; ROWS = m.rows;
    pos.col = p.col; pos.row = p.row;
    posInfo.value = { col: p.col, row: p.row };
    nerve.value = p.nerve;
    exploredSet = new Set(p.exploredCells ?? []);

    // Mafia turf partition comes baked onto every tile (districtId); labels sit
    // on each stronghold (the objective), matching the server's ownership.
    hydrateMap(m);
    checkTurfEntry(true); // opening the map is not a crossing — just record where we are

    const canvas = canvasRef.value!;
    ro = new ResizeObserver(() => {
      if (!centered && canvas.clientWidth >= 10) { centerOnHero(); centered = true; maybeFocusPan(); }
      draw();
    });
    ro.observe(canvas);
    if (canvas.clientWidth >= 10) { centerOnHero(); centered = true; maybeFocusPan(); }
    draw();

    // Throttled rAF loop for mob bob (~15 FPS)
    let last = 0;
    const loop = (t: number) => {
      if (t - last >= 66) { last = t; animTime = t; draw(); }
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
  } catch (err) {
    error.value = (err as Error).message;
  } finally {
    loading.value = false;
  }
});

onUnmounted(() => {
  if (rafId) cancelAnimationFrame(rafId);
  cancelAnimationFrame(panRaf);
  window.clearTimeout(confirmArmTimer);
  ro?.disconnect();
});
</script>

<template>
  <div class="relative w-full h-full bg-bg overflow-hidden">
    <canvas ref="canvasRef" class="block w-full h-full"
            style="cursor: crosshair; touch-action: none"
            @pointerdown="onPointerDown" @pointermove="onPointerMove"
            @pointerup="onPointerUp" @pointerleave="onPointerLeave" @wheel="onWheel"></canvas>

    <!-- territory-entry calling card -->
    <Transition name="turf-notice">
      <div v-if="turfNotice" class="cm-turf-notice corner-frame relative" :class="`is-${turfNotice.danger}`" role="status">
        <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
        <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
        <img v-if="turfNotice.crest" :src="turfNotice.crest" class="cm-tn-crest" alt="" />
        <div class="cm-tn-body">
          <div class="cm-tn-kicker">You've crossed into</div>
          <div class="cm-tn-name">{{ turfNotice.d.name }}</div>
          <div class="cm-tn-meta">
            <span>{{ turfNotice.d.family }}</span>
            <span class="cm-tn-dot">◆</span>
            <span class="cm-tn-tier">Tier {{ ROMAN[turfNotice.d.tier - 1] ?? turfNotice.d.tier }}</span>
            <span v-if="turfNotice.d.owner === 'you'" class="cm-tn-yours">· your turf</span>
          </div>
          <div class="cm-tn-warn">
            <template v-if="turfNotice.danger === 'deadly'">Word on the street: their soldiers are far above your weight. Walk carefully — or walk away.</template>
            <template v-else-if="turfNotice.danger === 'risky'">Their soldiers hit above your weight here. Pick your fights.</template>
            <template v-else-if="turfNotice.d.owner === 'you'">The streets tip their hats. Collect what you're owed.</template>
            <template v-else>You can handle what these streets throw at you.</template>
            <template v-if="turfNotice.d.locked"> The stronghold won't open its books until LV {{ turfNotice.d.unlockLevel }}.</template>
          </div>
        </div>
        <button class="btn-deco btn-deco--ghost btn-deco-icon cm-tn-close" title="Dismiss" @click="turfNotice = null">✕</button>
      </div>
    </Transition>

    <!-- top-left HUD plaque — nerve gauge + street plate -->
    <div class="absolute top-3 left-3 pointer-events-none select-none">
      <div class="cm-plaque corner-frame relative">
        <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
        <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
        <div class="cm-plaque-title">The City</div>
        <div class="cm-plaque-row flex items-center gap-3 mt-1.5">
          <div class="cm-nerve flex items-center gap-1.5" tabindex="0" role="img" :aria-label="`Nerve ${nerve.current} of ${nerve.max}`">
            <img :src="nerveIcon" alt="" class="w-4 h-4 object-contain shrink-0" />
            <span class="flex items-center gap-[3px]">
              <i v-for="n in Math.min(nerve.max, 12)" :key="n" class="cm-pip" :class="{ 'cm-pip-lit': n <= nerve.current }"></i>
            </span>
            <span class="font-heading text-sm text-text leading-none ml-0.5 whitespace-nowrap">{{ nerve.current }}<span class="text-text-dim">/{{ nerve.max }}</span><span v-if="nerveClock && !nerveClock.full" class="font-body text-[11px] tabular-nums text-text-dim"> · {{ fmtEta(nerveClock.nextIn) }}</span></span>
            <span class="cm-info" aria-hidden="true">i</span>
            <div class="cm-nerve-tip" role="tooltip">
              <b>Nerve</b> — your appetite for the streets. Moving across the map and starting fights both spend it. It creeps back up (+1 every 2.5 minutes<template v-if="nerveClock && !nerveClock.full">, full in {{ fmtEta(nerveClock.fullIn) }}</template>); when you're low, grab a Stiff Drink to refill.
            </div>
          </div>
          <span class="cm-divider"></span>
          <span class="cm-plate cm-loc" :title="`You're standing in ${locationName}`">&#9670; {{ locationName }}</span>
        </div>
      </div>
      <!-- status ribbon: permanent fixed-width intel line — only the text swaps,
           so the plaque column never moves a pixel -->
      <div class="cm-ribbon">
        <template v-if="error"><span class="text-error cm-ribbon-clip">{{ error }}</span></template>
        <template v-else-if="hoverName || hoverBlocked || hoverCost !== null">
          <span v-if="hoverName" class="text-gold cm-ribbon-clip">{{ hoverName }}</span>
          <span v-if="hoverBlocked" class="text-error shrink-0">No way through</span>
          <span v-else-if="hoverCost !== null" class="cm-move text-text-dim shrink-0">Move &middot; <span class="text-gold">{{ hoverCost }}</span><img :src="nerveIcon" alt="nerve" class="cm-move-ic" /></span>
        </template>
        <span v-else class="text-text-dim/50 italic normal-case tracking-normal font-body">Casing the streets&hellip;</span>
      </div>

      <!-- running on fumes? one-tap recovery, right where the gauges are -->
      <QuickRecover
        class="mt-2"
        :nerve="nerve.current" :max-nerve="nerve.max"
        :hp="props.heroHp" :max-hp="props.heroMaxHp" :full-hp="props.heroFullHp"
        :hp-regen="props.heroHpRegen" :nerve-regen="nerve.regen"
        :cash="props.cash" :gold-bars="props.goldBars"
        @updated="onRecovered"
      />
    </div>

    <p v-if="loading" class="absolute inset-0 grid place-items-center text-text-dim pointer-events-none">Drawing the city…</p>

    <!-- center-on-hero button -->
    <button class="btn-deco btn-deco--ghost btn-deco--sm absolute top-3 right-3"
            @click="centerOnHero">&#8982; Center</button>

    <!-- minimap -->
    <div class="pointer-events-none absolute bottom-3 right-3 rounded border border-border"
         style="background: rgba(10,7,20,0.8); box-shadow: var(--shadow-lift)">
      <canvas ref="minimapRef" width="148" height="148" class="block"></canvas>
    </div>

    <!-- "how to run the city" — tucked under a deco help button -->
    <div class="absolute bottom-3 left-3">
      <transition name="cm-help">
        <div v-if="showHelp" class="cm-help-card corner-frame relative mb-2">
          <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
          <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
          <div class="cm-plaque-title mb-2">Running the city</div>
          <ul class="space-y-1.5 font-body text-[13px] text-text-dim leading-snug">
            <li><span class="cm-help-key">&#9670;</span> <b class="cm-help-verb">Drag</b> &mdash; prowl the streets</li>
            <li><span class="cm-help-key">&#9670;</span> <b class="cm-help-verb">Scroll</b> &mdash; lean in, lean out</li>
            <li><span class="cm-help-key">&#9670;</span> <b class="cm-help-verb">Click a hex</b> &mdash; walk there (costs nerve)</li>
            <li><span class="cm-help-key">&#9670;</span> <b class="cm-help-verb">Click an enemy</b> &mdash; start trouble</li>
            <li><span class="cm-help-key">&#9670;</span> <b class="cm-help-verb">Stand on a stronghold</b> &mdash; take the turf</li>
          </ul>
          <p class="mt-2 pt-2 border-t border-border/60 font-body italic text-[11px] text-text-dim/80">
            Eight families run these streets, each in its own color. Highrise cores and the river don't move for anybody.
          </p>
        </div>
      </transition>
      <button class="btn-deco btn-deco-icon" :class="showHelp ? 'btn-deco--gold' : 'btn-deco--ghost'" title="How to run the city"
              @click="showHelp = !showHelp">?</button>
    </div>

    <!-- vignette frame -->
    <div class="pointer-events-none absolute inset-0"
         style="box-shadow: inset 0 0 140px 16px rgba(0,0,0,0.7)"></div>

    <!-- pre-fight calling card — details + confirm before any blood is spilled -->
    <div v-if="confirmFight" class="cm-cf-back" @click.self="confirmFight = null">
      <div class="cm-cf corner-frame relative" :class="confirmFight.kind === 'mob' ? confirmOdds?.cls : ''">
        <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
        <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>

        <template v-if="confirmFight.kind === 'mob'">
          <div class="cm-cf-mugwrap">
            <img v-if="mobArt(confirmFight.mob.type, confirmFight.mob.archetype)"
                 :src="mobArt(confirmFight.mob.type, confirmFight.mob.archetype)" class="cm-cf-mug" alt="" />
          </div>
          <div class="cm-cf-kicker">A challenge on the street</div>
          <div class="cm-cf-name">{{ confirmFight.mob.name }}</div>
          <div class="cm-cf-meta">
            <span>Lv {{ confirmFight.mob.level }}</span>
            <span class="cm-cf-dot">◆</span>
            <span class="cm-cf-odds">{{ confirmOdds?.label }}</span>
          </div>
          <div class="cm-cf-stats">
            <div><b>{{ confirmFight.mob.hp }}</b><i>Health</i></div>
            <div><b>{{ confirmFight.mob.attack }}</b><i>Attack</i></div>
            <div><b>{{ confirmFight.mob.defense }}</b><i>Defense</i></div>
          </div>
          <div class="cm-cf-pay">
            <img :src="cashIcon" alt="" /><span>+{{ confirmFight.mob.cash.toLocaleString() }} cash</span>
            <span class="cm-cf-dot">·</span><span>+{{ confirmFight.mob.exp.toLocaleString() }} XP</span>
          </div>
          <p class="cm-cf-note">{{ confirmOdds?.note }}</p>
          <p v-if="heroHpPct !== null && heroHpPct < 40" class="cm-cf-hpwarn">
            You're at {{ heroHpPct }}% health — consider patching up first.
          </p>
        </template>

        <template v-else>
          <div class="cm-cf-mugwrap">
            <img v-if="crestArt(confirmFight.d.name)" :src="crestArt(confirmFight.d.name)" class="cm-cf-crest" alt="" />
          </div>
          <div class="cm-cf-kicker">Turf takeover</div>
          <div class="cm-cf-name">{{ confirmFight.d.name }}</div>
          <div class="cm-cf-meta">
            <span>{{ confirmFight.d.family }}</span>
            <span class="cm-cf-dot">◆</span>
            <span>Tier {{ ROMAN[confirmFight.d.tier - 1] ?? confirmFight.d.tier }}</span>
          </div>
          <p class="cm-cf-note">
            Beat the {{ confirmFight.d.family }} garrison and the flag over {{ confirmFight.d.name }} changes tonight.
          </p>
          <p v-if="heroHpPct !== null && heroHpPct < 40" class="cm-cf-hpwarn">
            You're at {{ heroHpPct }}% health — consider patching up first.
          </p>
        </template>

        <div class="cm-cf-actions">
          <button class="btn-deco btn-deco--ghost" @click="confirmFight = null">Walk away</button>
          <button class="btn-deco btn-deco--gold cm-cf-go" :disabled="!confirmArmed" @click="onConfirmFight">
            <span>{{ confirmFight.kind === 'mob' ? 'Start the fight' : 'Take the turf' }}</span>
            <span class="cm-cf-cost"><img :src="nerveIcon" alt="" />1 nerve</span>
          </button>
        </div>
      </div>
    </div>

    <!-- interactive tactical fight — turn-by-turn against the server -->
    <FightScreen
      v-if="tacticalTarget"
      :col="tacticalTarget.col"
      :row="tacticalTarget.row"
      :hero-class="props.heroClass"
      @done="onTacticalDone"
    />

    <!-- interactive turf takeover — same tactical arena, vs the garrison -->
    <FightScreen
      v-if="garrisonTarget"
      :garrison-id="garrisonTarget"
      :hero-class="props.heroClass"
      @done="onCaptureDone"
    />
  </div>
</template>

<style scoped>
/* ── map HUD deco ─────────────────────────────────────────────── */
.cm-plaque {
  /* z-index lifts the plaque (and its Nerve tooltip) above the QuickRecover
     pill below — both create stacking contexts (backdrop-filter here, the
     pulsing drop-shadow there), so without this the pill pokes through the tip */
  position: relative; z-index: 30;
  padding: 0.55rem 0.9rem 0.65rem;
  border: 1px solid color-mix(in srgb, var(--color-gold) 35%, transparent);
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(16,13,22,0.92), rgba(10,7,16,0.88));
  box-shadow: 0 4px 18px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04);
  backdrop-filter: blur(3px);
}
.cm-plaque-title {
  font-family: var(--font-heading, serif);
  font-size: 15px; line-height: 1; color: var(--color-gold);
  letter-spacing: 0.06em;
}
.cm-pip {
  width: 7px; height: 7px; transform: rotate(45deg); border-radius: 1px;
  background: rgba(255,255,255,0.08); border: 1px solid rgba(200,162,76,0.35);
  transition: background 200ms ease, box-shadow 200ms ease;
}
.cm-pip-lit {
  background: linear-gradient(135deg, var(--color-gold-bright), var(--color-gold));
  box-shadow: 0 0 5px rgba(227,200,120,0.5);
}
.cm-divider { width: 1px; height: 16px; background: linear-gradient(180deg, transparent, var(--color-border), transparent); }
/* nerve gauge — hoverable helper tooltip (the plaque wrapper is pointer-events:none) */
.cm-nerve { position: relative; pointer-events: auto; cursor: help; }
.cm-info {
  display: inline-grid; place-items: center; width: 14px; height: 14px; margin-left: 3px;
  font-family: var(--font-label, sans-serif); font-size: 9px; font-style: italic; line-height: 1;
  color: var(--color-gold); border: 1px solid color-mix(in srgb, var(--color-gold) 50%, transparent);
  border-radius: 999px;
}
.cm-nerve-tip {
  position: absolute; top: calc(100% + 9px); left: 0; z-index: 40;
  width: 244px; padding: 10px 12px;
  font-family: var(--font-body, serif); font-size: 12.5px; letter-spacing: normal; text-transform: none;
  line-height: 1.45; color: var(--color-text-dim);
  background: var(--color-surface-2, #100e14);
  border: 1px solid color-mix(in srgb, var(--color-gold) 45%, transparent);
  border-radius: 8px; box-shadow: 0 8px 22px rgba(0,0,0,0.6);
  opacity: 0; visibility: hidden; transform: translateY(-4px);
  transition: opacity 140ms ease, transform 140ms ease, visibility 140ms;
}
.cm-nerve-tip::before {
  content: ''; position: absolute; bottom: 100%; left: 14px;
  border: 6px solid transparent; border-bottom-color: color-mix(in srgb, var(--color-gold) 45%, transparent);
}
.cm-nerve-tip b { color: var(--color-gold-bright); font-weight: 400; }
.cm-nerve:hover .cm-nerve-tip, .cm-nerve:focus-visible .cm-nerve-tip { opacity: 1; visibility: visible; transform: none; }
/* move-cost readout — keep the number and its icon on one line */
.cm-move { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
.cm-move-ic { width: 12px; height: 12px; object-fit: contain; flex: none; }
.cm-plate {
  font-family: var(--font-label, sans-serif); font-size: 11px; letter-spacing: 0.14em;
  color: var(--color-text-dim);
  padding: 2px 8px; border: 1px solid var(--color-border); border-radius: 4px;
  background: rgba(255,255,255,0.03);
  font-variant-numeric: tabular-nums;
}
.cm-loc {
  min-width: 0; max-width: 170px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  color: var(--color-gold); letter-spacing: 0.06em;
  border-color: color-mix(in srgb, var(--color-gold) 30%, transparent);
}
.cm-plaque { width: 372px; }

/* ── MOBILE HUD ── the plaque + ribbon shrink to leave room for ⌖ Center,
   and the nerve/location row is allowed to wrap inside the plaque */
@media (max-width: 640px) {
  .cm-plaque, .cm-ribbon { width: calc(100vw - 122px); max-width: 372px; }
  .cm-plaque-row { flex-wrap: wrap; row-gap: 4px; }
  .cm-plaque-row .cm-divider { display: none; }
  .cm-loc { max-width: 100%; }
}
.cm-ribbon {
  display: flex; align-items: center; gap: 0.9rem;
  height: 26px; width: 372px; margin-top: 6px; padding: 0 12px;
  font-family: var(--font-label, sans-serif); font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
  border-left: 2px solid color-mix(in srgb, var(--color-gold) 65%, transparent);
  background: linear-gradient(90deg, rgba(10,7,16,0.88), rgba(10,7,16,0.35));
  border-radius: 0 4px 4px 0;
  white-space: nowrap; overflow: hidden;
}
.cm-ribbon-clip { overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.cm-help-verb { color: var(--color-text); font-weight: 600; }
.cm-help-card {
  width: 300px; padding: 0.9rem 1rem;
  border: 1px solid color-mix(in srgb, var(--color-gold) 35%, transparent);
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(16,13,22,0.96), rgba(10,7,16,0.94));
  box-shadow: 0 10px 30px rgba(0,0,0,0.7);
  backdrop-filter: blur(4px);
}
.cm-help-key { display: inline-block; width: 1.4em; color: var(--color-gold); }
.cm-help-enter-active, .cm-help-leave-active { transition: opacity 180ms ease, transform 180ms ease; }
.cm-help-enter-from, .cm-help-leave-to { opacity: 0; transform: translateY(6px); }
@media (prefers-reduced-motion: reduce) {
  .cm-help-enter-active, .cm-help-leave-active { transition: none; }
}

/* ── territory-entry calling card ─────────────────────────────────── */
.cm-turf-notice {
  --tn: var(--color-gold);
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 14px;
  max-width: min(480px, calc(100% - 24px));
  padding: 14px 16px;
  background: linear-gradient(165deg, rgba(22, 18, 28, 0.94), rgba(10, 8, 14, 0.97));
  border: 1px solid color-mix(in srgb, var(--tn) 40%, transparent);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.65), 0 0 30px color-mix(in srgb, var(--tn) 10%, transparent);
  backdrop-filter: blur(5px);
}
.cm-turf-notice .corner-bracket { --cb: var(--tn); }
.cm-turf-notice.is-risky { --tn: #d9a441; }
.cm-turf-notice.is-deadly { --tn: var(--color-error); }

.cm-tn-crest {
  width: 52px;
  height: 52px;
  flex: none;
  object-fit: contain;
  filter: drop-shadow(0 3px 8px rgba(0, 0, 0, 0.6));
}
.cm-tn-kicker {
  font-family: var(--font-label);
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--color-text-dim);
}
.cm-tn-name {
  font-family: var(--font-heading);
  font-size: 21px;
  line-height: 1.15;
  color: var(--color-text);
}
.cm-tn-meta {
  display: flex;
  align-items: baseline;
  gap: 7px;
  margin-top: 1px;
  font-family: var(--font-label);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--tn) 85%, var(--color-text));
}
.cm-tn-dot { font-size: 7px; color: var(--tn); }
.cm-tn-tier { color: var(--color-gold-bright); }
.cm-tn-yours { color: var(--color-gold-bright); }
.cm-tn-warn {
  margin-top: 5px;
  font-family: var(--font-body);
  font-size: 13.5px;
  line-height: 1.35;
  color: color-mix(in srgb, var(--tn) 55%, var(--color-text));
}
.cm-tn-close { flex: none; align-self: flex-start; margin-left: 2px; }

.turf-notice-enter-active { transition: opacity 300ms ease, transform 300ms ease; }
.turf-notice-leave-active { transition: opacity 240ms ease, transform 240ms ease; }
.turf-notice-enter-from { opacity: 0; transform: translate(-50%, -14px); }
.turf-notice-leave-to { opacity: 0; transform: translate(-50%, -8px); }
@media (prefers-reduced-motion: reduce) {
  .turf-notice-enter-active, .turf-notice-leave-active { transition: none; }
}

/* ── pre-fight calling card ─────────────────────────────────────────────── */
.cm-cf-back {
  position: absolute; inset: 0; z-index: 60;
  display: grid; place-items: center; padding: 16px;
  background: rgba(6, 4, 12, 0.72);
  backdrop-filter: blur(3px); -webkit-backdrop-filter: blur(3px);
}
.cm-cf {
  width: min(380px, 92%);
  padding: 22px 22px 18px;
  text-align: center;
  border: 1px solid color-mix(in srgb, var(--color-gold, #c9a84c) 45%, transparent);
  border-radius: 12px;
  background:
    radial-gradient(120% 90% at 50% 0%, rgba(200, 162, 76, 0.10), transparent 55%),
    var(--color-surface, #14121a);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.7);
  animation: cm-cf-rise 220ms ease both;
}
@keyframes cm-cf-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .cm-cf { animation: none; } }
.cm-cf.is-risky { border-color: color-mix(in srgb, #d99a3d 55%, transparent); }
.cm-cf.is-deadly { border-color: color-mix(in srgb, var(--color-error, #b3413e) 65%, transparent); }
.cm-cf-mugwrap { display: flex; justify-content: center; margin-bottom: 8px; }
.cm-cf-mug {
  width: 84px; height: 84px; border-radius: 999px; object-fit: cover; object-position: top;
  border: 2px solid color-mix(in srgb, var(--color-gold, #c9a84c) 70%, transparent);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.6);
  background: #0c0a11;
}
.cm-cf-crest {
  width: 84px; height: 84px; object-fit: contain;
  filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.65));
}
.cm-cf-kicker { font-family: var(--font-label, sans-serif); text-transform: uppercase; letter-spacing: 0.22em; font-size: 10px; color: var(--color-text-dim, #9a8f7a); }
.cm-cf-name { font-family: var(--font-heading, serif); font-size: 26px; line-height: 1.15; color: var(--color-gold-bright, #e9c97a); margin: 2px 0 3px; }
.cm-cf-meta { display: flex; justify-content: center; align-items: center; gap: 8px; font-family: var(--font-label, sans-serif); text-transform: uppercase; letter-spacing: 0.12em; font-size: 11px; color: var(--color-text-dim, #9a8f7a); }
.cm-cf-dot { color: var(--color-gold, #c9a84c); font-size: 9px; }
.is-risky .cm-cf-odds { color: #d99a3d; }
.is-deadly .cm-cf-odds { color: var(--color-error, #b3413e); }
.is-safe .cm-cf-odds { color: var(--color-success, #7fb79c); }
.cm-cf-stats {
  display: grid; grid-template-columns: repeat(3, 1fr);
  margin: 14px 0 0;
  border: 1px solid var(--color-border, #2a2436); border-radius: 8px; overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
}
.cm-cf-stats > div { display: flex; flex-direction: column; gap: 2px; padding: 8px 4px; }
.cm-cf-stats > div + div { border-left: 1px solid var(--color-border, #2a2436); }
.cm-cf-stats b { font-family: var(--font-heading, serif); font-weight: 600; font-size: 16px; color: var(--color-text, #ece3cf); line-height: 1.1; }
.cm-cf-stats i { font-style: normal; font-family: var(--font-label, sans-serif); font-size: 9px; text-transform: uppercase; letter-spacing: 0.12em; color: var(--color-text-dim, #9a8f7a); }
.cm-cf-pay {
  display: flex; justify-content: center; align-items: center; gap: 7px;
  margin-top: 9px; font-family: var(--font-label, sans-serif); font-size: 11.5px;
  letter-spacing: 0.06em; color: var(--color-gold, #c9a84c);
}
.cm-cf-pay img { width: 16px; height: 16px; object-fit: contain; }
.cm-cf-note { font-family: var(--font-body, serif); font-style: italic; font-size: 13px; color: var(--color-text-dim, #9a8f7a); margin-top: 10px; line-height: 1.45; }
.cm-cf-hpwarn { font-family: var(--font-body, serif); font-size: 12.5px; color: var(--color-error, #b3413e); margin-top: 6px; }
/* wrap: the nowrap plaques ("Start the fight · 1 nerve") can outgrow the card
   on phones — let them stack to full-width rows instead of overflowing it */
.cm-cf-actions { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin-top: 16px; }
.cm-cf-actions > * { flex: 1 1 auto; }
/* the fight/takeover button states its nerve price so the spend is never a surprise */
.cm-cf-go { display: inline-flex; align-items: center; gap: 9px; }
.cm-cf-cost {
  display: inline-flex; align-items: center; gap: 3px;
  padding-left: 9px; border-left: 1px solid color-mix(in srgb, currentColor 35%, transparent);
  font-family: var(--font-label, monospace); font-size: 10px; letter-spacing: 0.06em;
  opacity: 0.92;
}
.cm-cf-cost img { width: 12px; height: 12px; object-fit: contain; }
</style>
